import { Box, Chip, SxProps, Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';

type ChoiceChipsProps<T extends string | number> = Readonly<{
  id: string;
  label: string;
  options: readonly Readonly<{ value: T; label: string }>[];
  value: T;
  onChange: (value: T) => void;
}>;

export const ChoiceChips = <T extends string | number>({
  id,
  label,
  options,
  value,
  onChange,
}: ChoiceChipsProps<T>) => (
  <Box sx={groupSx} role="radiogroup" aria-label={label}>
    {options.map((option) => {
      const selected = option.value === value;

      return (
        <Chip
          key={option.value}
          id={`${id}-${option.value}`}
          role="radio"
          aria-checked={selected}
          variant="pill"
          size="md"
          clickable
          label={option.label}
          onClick={() => onChange(option.value)}
          sx={[chipBaseSx, selected ? selectedSx : unselectedSx] as SxProps<Theme>}
        />
      );
    })}
  </Box>
);

const groupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const chipBaseSx = ({ backrs }: Theme): SystemStyleObject<Theme> => ({
  paddingX: backrs.spacing['spacing-md'],
});

const unselectedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  '&:hover': { backgroundColor: backrs.colors['Colors/Background/bg-secondary'] },
});

const selectedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Brand/500']}`,
  backgroundColor: backrs.colors['Brand/500'],
  '&:hover': { backgroundColor: backrs.colors['Brand/600'] },
});
